import React, { useState, useEffect, useRef, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { UserCircleIcon } from '@heroicons/react/24/outline';
import { navigation, businessInfo } from '../../config/content';
import { images } from '../../config/assets';
import { AuthContext } from '../../contexts/AuthContext';
import { services, serviceCategories } from '../../data/services';
import { coverageZones, coverageCategories } from '../../data/coverage';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [coverageOpen, setCoverageOpen] = useState(false);
  const servicesRef = useRef<HTMLDivElement>(null);
  const coverageRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const { user, signOut } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
      if (servicesRef.current && !servicesRef.current.contains(event.target as Node)) {
        setServicesOpen(false);
      }
      if (coverageRef.current && !coverageRef.current.contains(event.target as Node)) {
        setCoverageOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await signOut();
    setIsProfileOpen(false);
    setIsMenuOpen(false);
    navigate('/');
  };

  return (
    <header className={`${isSticky ? 'fixed' : 'absolute'} left-0 right-0 transition-all duration-300`} style={{ top: isSticky ? '0' : '20px', zIndex: 99 }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
        <div className={`flex justify-between items-center w-full h-auto bg-white rounded-[50px] transition-shadow duration-300 ${isSticky ? 'shadow-md' : 'shadow-sm'}`} style={{ paddingLeft: '20px', paddingRight: '20px', paddingTop: '20px', paddingBottom: '20px' }}>
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img
              src={images.logo}
              alt={businessInfo.name}
              width={180}
              height={53}
              className="h-[45px] sm:h-[52.97px] w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center" style={{ gap: '8px' }}>
            {navigation.main.map((item) => {
              // Services gets a dropdown
              if (item.href === '/services') {
                return (
                  <div key={item.href} ref={servicesRef} style={{ position: 'relative' }}>
                    <button
                      onClick={() => { setServicesOpen(!servicesOpen); setCoverageOpen(false); }}
                      className="text-black font-normal hover:text-speedy-blue-alt transition-colors text-sm xl:text-base"
                      style={{ padding: '0 8px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontFamily: 'inherit' }}
                      aria-expanded={servicesOpen}
                      aria-haspopup="menu"
                      aria-label={`${item.label} menu`}
                    >
                      {item.label}
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true" style={{ transform: servicesOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>
                        <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>

                    {servicesOpen && (
                      <div style={{
                        position: 'absolute', top: 'calc(100% + 12px)', left: '50%', transform: 'translateX(-50%)',
                        width: 680, background: '#fff', borderRadius: 16, border: '1px solid #e5e7eb',
                        boxShadow: '0 12px 40px rgba(0,0,0,0.1)', zIndex: 50, padding: 24,
                        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20,
                      }}>
                        {serviceCategories.map(cat => {
                          const catServices = services.filter(s => s.category === cat.key);
                          return (
                            <div key={cat.key}>
                              <Link
                                to="/services"
                                onClick={() => setServicesOpen(false)}
                                style={{ fontSize: 11, fontWeight: 700, color: '#395ACB', letterSpacing: '1px', textTransform: 'uppercase', textDecoration: 'none', display: 'block', marginBottom: 10 }}
                              >
                                {cat.title}
                              </Link>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {catServices.map(svc => (
                                  <Link
                                    key={svc.slug}
                                    to={`/services/${svc.slug}`}
                                    onClick={() => setServicesOpen(false)}
                                    style={{
                                      display: 'block', padding: '7px 10px', borderRadius: 8,
                                      fontSize: 13, fontWeight: 500, color: '#374151', textDecoration: 'none',
                                      transition: 'background 0.15s',
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.background = '#f0f4ff'; e.currentTarget.style.color = '#395ACB'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#374151'; }}
                                  >
                                    {svc.title}
                                  </Link>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                        {/* View all link */}
                        <div style={{ gridColumn: '1 / -1', borderTop: '1px solid #e5e7eb', paddingTop: 14, marginTop: 4, textAlign: 'center' }}>
                          <Link
                            to="/services"
                            onClick={() => setServicesOpen(false)}
                            style={{ fontSize: 13, fontWeight: 600, color: '#395ACB', textDecoration: 'none' }}
                          >
                            View All Services →
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              // Coverage gets a dropdown
              if (item.href === '/coverage') {
                return (
                  <div key={item.href} ref={coverageRef} style={{ position: 'relative' }}>
                    <button
                      onClick={() => { setCoverageOpen(!coverageOpen); setServicesOpen(false); }}
                      className="text-black font-normal hover:text-speedy-blue-alt transition-colors text-sm xl:text-base"
                      style={{ padding: '0 8px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontFamily: 'inherit' }}
                      aria-expanded={coverageOpen}
                      aria-haspopup="menu"
                      aria-label={`${item.label} menu`}
                    >
                      {item.label}
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true" style={{ transform: coverageOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>
                        <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>

                    {coverageOpen && (
                      <div style={{
                        position: 'absolute', top: 'calc(100% + 12px)', left: '50%', transform: 'translateX(-50%)',
                        width: 560, background: '#fff', borderRadius: 16, border: '1px solid #e5e7eb',
                        boxShadow: '0 12px 40px rgba(0,0,0,0.1)', zIndex: 50, padding: 24,
                        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16,
                      }}>
                        {coverageCategories.map(cat => {
                          const zones = coverageZones.filter(z => z.category === cat.key);
                          return (
                            <div key={cat.key}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#395ACB', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 8 }}>
                                {cat.title}
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {zones.map(zone => (
                                  <Link
                                    key={zone.slug}
                                    to={`/coverage/${zone.slug}`}
                                    onClick={() => setCoverageOpen(false)}
                                    style={{
                                      display: 'block', padding: '6px 10px', borderRadius: 8,
                                      fontSize: 13, fontWeight: 500, color: '#374151', textDecoration: 'none',
                                      transition: 'background 0.15s',
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.background = '#f0f4ff'; e.currentTarget.style.color = '#395ACB'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#374151'; }}
                                  >
                                    {zone.title}
                                  </Link>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                        <div style={{ gridColumn: '1 / -1', borderTop: '1px solid #e5e7eb', paddingTop: 12, marginTop: 4, textAlign: 'center' }}>
                          <Link to="/coverage" onClick={() => setCoverageOpen(false)} style={{ fontSize: 13, fontWeight: 600, color: '#395ACB', textDecoration: 'none' }}>
                            View Full Coverage Map →
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className="text-black font-normal hover:text-speedy-blue-alt transition-colors text-sm xl:text-base"
                  style={{
                    padding: '0 8px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.label}
                </Link>
              );
            })}
            {/* Track button (sits next to nav links — same height as primary CTAs) */}
            <Link
              to="/track"
              data-track="track_click"
              data-track-source="header"
              className="inline-flex items-center justify-center gap-1.5 transition-all duration-200 hover:bg-blue-50 text-sm"
              style={{
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: 600,
                color: '#395ACB',
                border: '1.5px solid #395ACB',
                borderRadius: '8px',
                background: 'transparent',
                marginLeft: 8,
                lineHeight: 1.2,
              }}
              title="Track your package"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" />
              </svg>
              Track
            </Link>

            {/* Separator */}
            <div style={{ width: 1, height: 24, background: '#e5e7eb', margin: '0 4px 0 8px' }} />

            {!user ? (
              <>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center transition-all duration-200 hover:bg-gray-100 text-sm"
                  style={{
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#374151',
                    borderRadius: '8px',
                    lineHeight: 1.2,
                  }}
                >
                  Log In
                </Link>
                <Link
                  to="/signup"
                  className="inline-flex items-center justify-center transition-all duration-200 text-sm"
                  style={{
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#395ACB',
                    border: '1.5px solid #395ACB',
                    borderRadius: '8px',
                    background: 'transparent',
                    lineHeight: 1.2,
                  }}
                >
                  Sign Up
                </Link>
              </>
            ) : (
              <div className="relative" ref={profileRef}>
                <button
                  onClick={() => setIsProfileOpen(!isProfileOpen)}
                  className="flex items-center gap-2 text-black hover:text-speedy-blue-alt transition-colors"
                >
                  <UserCircleIcon className="h-8 w-8" />
                  <span className="text-sm font-normal hidden xl:inline max-w-[140px] truncate">
                    {user.user_metadata?.full_name || user.email}
                  </span>
                </button>
                {isProfileOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-200 py-3 z-50">
                    <div className="px-4 pb-3 border-b border-gray-100">
                      <p className="text-xs text-gray-500">Signed in as</p>
                      <p className="text-sm font-medium text-black truncate">{user.email}</p>
                    </div>
                    <div className="pt-2 px-2">
                      <Link
                        to="/portal"
                        onClick={() => setIsProfileOpen(false)}
                        className="block w-full text-left px-3 py-2 text-sm text-speedy-blue-alt hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        Dashboard
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            <Link
              to="/order"
              data-track="book_click"
              data-track-source="header"
              className="inline-flex items-center justify-center text-white transition-all duration-200 hover:shadow-lg text-sm"
              style={{
                padding: '8px 20px',
                fontSize: '14px',
                fontWeight: 700,
                background: '#395ACB',
                borderRadius: '8px',
                marginLeft: 4,
                border: '1.5px solid #395ACB',
                lineHeight: 1.2,
              }}
            >
              Place Order
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 text-black"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <nav className="lg:hidden absolute left-0 right-0 bg-white mx-4 rounded-[20px] shadow-lg py-3 px-3 max-h-[80vh] overflow-y-auto" style={{ top: isSticky ? '80px' : '100px' }}>
            {navigation.main.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className="block py-2.5 px-4 text-black text-sm font-medium hover:text-speedy-blue-alt hover:bg-gray-50 rounded-lg transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}
            <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
              {!user ? (
                <>
                  <Link
                    to="/login"
                    className="block text-center text-black border-2 border-gray-300 rounded-[50px] py-2.5 px-4 text-sm font-medium hover:bg-gray-50 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Login
                  </Link>
                  <Link
                    to="/signup"
                    className="block text-center bg-transparent text-speedy-blue-alt border-2 border-speedy-blue-alt rounded-[50px] py-2.5 px-4 text-sm font-medium hover:bg-speedy-blue-alt hover:text-white transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Sign Up
                  </Link>
                </>
              ) : (
                <>
                  <div className="px-4 py-2">
                    <p className="text-xs text-gray-500">Signed in as</p>
                    <p className="text-sm font-medium text-black truncate">{user.email}</p>
                  </div>
                  <Link
                    to="/portal"
                    className="block text-center bg-transparent text-speedy-blue-alt border-2 border-speedy-blue-alt rounded-[50px] py-2.5 px-4 text-sm font-medium hover:bg-speedy-blue-alt hover:text-white transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="block w-full text-center text-red-600 border-2 border-red-300 rounded-[50px] py-2.5 px-4 text-sm font-medium hover:bg-red-50 transition-colors"
                  >
                    Logout
                  </button>
                </>
              )}
              <Link
                to="/track"
                data-track="track_click"
                data-track-source="mobile_menu"
                className="block text-center text-speedy-blue-alt border-2 border-speedy-blue-alt rounded-[50px] py-2.5 px-4 text-sm font-medium hover:bg-blue-50 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                📦 Track Order
              </Link>
              <Link
                to="/order"
                data-track="book_click"
                data-track-source="mobile_menu"
                className="block text-center bg-speedy-blue-alt text-white rounded-[50px] py-2.5 px-4 text-sm font-medium transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Place Order
              </Link>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
